import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  ArrowRight,
  Radar,
  FileText,
  Code2,
  CheckCircle2,
  Send,
  Database,
  Network,
  TrendingUp,
} from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { GlassIcon } from "@/components/glass-icon";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { MetricCounters } from "@/components/metric-counters";
import { OrbitLoop } from "@/components/orbit-loop";
import { TestimonialsMasonry } from "@/components/testimonials-masonry";
import { LandingHeader } from "@/components/landing-header";
import { TrustStrip } from "@/components/trust-strip";
import { ProblemSplit } from "@/components/problem-split";
import { Input } from "@/components/ui/input";
import { useCalendlyPopup } from "@/hooks/use-calendly-popup";
import { attachCalendlyTracking } from "@/lib/analytics/calendly-tracking";
import { seo } from "@/lib/seo";
import { breadcrumb, webPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { signUpUrl, scrollToSection, CALENDLY_30MIN_URL } from "@/lib/cta";

const LP_PATH = "/lp/intelligent-pr";

const LP_TITLE = "Programmatic Backlinks and Domain Authority";
const LP_DESCRIPTION =
  "Axy.digital researches and generates press releases, then programmatically distributes them across premier media networks to secure placements, high-quality citations, organic backlinks, and AI search visibility on autopilot.";

export const Route = createFileRoute("/lp/intelligent-pr/")({
  head: () =>
    seo({
      title: LP_TITLE,
      description: LP_DESCRIPTION,
      path: LP_PATH,
      type: "website",
      jsonLd: [
        webPage({
          name: LP_TITLE,
          description: LP_DESCRIPTION,
          url: `${SITE_URL}${LP_PATH}`,
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: LP_TITLE, url: LP_PATH },
        ]),
      ],
    }),
  component: LandingPage,
});

const PILLARS = [
  {
    label: "Zero-Effort Data Extraction",
    description:
      "Axy.digital automatically mines the live market narratives and buyer intent queries already passing through your workspace engine. It programmatically drafts deep, technical thought leadership reports containing the data structures that search algorithms actively reward.",
    icon: Database,
  },
  {
    label: "High-Authority Syndication Networks",
    description:
      "Your reports are algorithmically deployed across high-authority newswire syndication networks. By bypassing the human gatekeepers and going straight to high-indexing citation nodes, you secure a defensible layer of high-quality, permanent backlinks.",
    icon: Network,
  },
  {
    label: "Programmatic SEO & Crunchbase Lift",
    description:
      "This isn't about vanity metrics — it's about programmatic signals. Systematic, data-rich report drops create a high-frequency citation footprint that forces rapid upward movement in your organic Google rankings, GEO model references, and Crunchbase profile authority.",
    icon: TrendingUp,
  },
];

const PERFORMANCE_MATRIX = [
  {
    label: "What it builds",
    value: "Permanent Backlinks",
    proof: "Boosts your Google and AI search authority",
  },
  {
    label: "How it publishes",
    value: "Direct Newswire Syndication",
    proof: "Skips the human gatekeepers entirely",
  },
  {
    label: "Your effort",
    value: "1-Click Approvals",
    proof: "Just review the report draft and hit send",
  },
];

function ReportPreviewForm() {
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = new URL(signUpUrl("lp-intelligent-pr-report-preview"));
    if (domain) url.searchParams.set("domain", domain);
    if (email) url.searchParams.set("email", email);
    window.location.href = url.toString();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full">
      <Input
        type="text"
        required
        placeholder="yourcompany.com"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <Input
        type="email"
        required
        placeholder="business@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <button
        type="submit"
        className="btn-gradient hover:brightness-110 transition-all rounded-full px-6 h-12 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap"
      >
        Generate My Free Market Report Preview
        <ArrowRight className="size-4" />
      </button>
    </form>
  );
}

function LandingPage() {
  const openCalendlyPopup = useCalendlyPopup();
  useEffect(() => attachCalendlyTracking(), []);
  const openCalendly = () => openCalendlyPopup(CALENDLY_30MIN_URL);
  return (
    <>
      <LandingHeader ctaLabel="Chat with us" onClick={openCalendly} />

      {/* HERO */}
      <section className="relative px-6 pt-20 pb-24 overflow-hidden">
        <AuroraBackground />
        <div
          aria-hidden
          className="absolute left-1/2 top-24 -translate-x-1/2 opacity-60 pointer-events-none"
        >
          <GlassOrb size="lg" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <SectionEyebrow className="justify-center text-center">
            Intelligent PR · Programmatic backlinks · Domain authority on autopilot
          </SectionEyebrow>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white text-balance mb-6">
            Programmatic Backlinks and Domain Authority
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto mb-10 text-pretty">
            Axy.digital researches and generates press releases, then programmatically distributes
            them across premier media networks to secure placements, high-quality citations, organic
            backlinks, and AI search visibility on autopilot.
          </p>
          <div className="flex flex-col items-center gap-3">
            <Link
              to="/lp/intelligent-pr/pricing"
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Deploy Intelligent PR
              <ArrowRight className="size-4" />
            </Link>
            <p className="text-xs text-white/55">
              Onboarding opens weekly · Protected domain allocation
            </p>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* METRICS BANNER */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <SectionEyebrow>Intelligent PR by Axy.digital</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-3 max-w-3xl">
            A relentless citation engine that compounds your domain authority every single week.
          </h2>
          <p className="text-white/65 max-w-2xl mb-10">
            Built for founders and operators who want permanent, high-equity backlinks — without the
            agency retainer, pitch cycles, or vanity coverage.
          </p>
          <MetricCounters
            items={[
              {
                value: 200,
                prefix: "+",
                label: "Syndication endpoints",
                proof: "Premium newswire & citation networks",
              },
              {
                value: 145,
                prefix: "+",
                label: "Guaranteed placements",
                proof: "Per weekly report drop",
              },
              {
                value: 16,
                label: "Reports drafted monthly",
                proof: "Tier-1 + Tier-2 distribution mix",
              },
              {
                value: 11,
                suffix: "×",
                label: "Authority lift",
                proof: "Across 90-day rollouts",
              },
            ]}
          />
        </div>
      </section>

      {/* PROBLEM */}
      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>The Problem</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              Vanity press clips don't move <span className="text-gradient">domain authority.</span>
            </h2>
            <p className="mt-4 text-lg text-white/70 max-w-2xl">
              Traditional PR firms chase headlines that look great in a deck — but generate zero
              backlinks, zero AI citations, and zero compounding SEO equity. Programmatic
              syndication does the opposite.
            </p>
          </div>
          <ProblemSplit />
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative px-6 py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 40% at 30% 20%, rgba(123,63,242,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <SectionEyebrow>The intelligent PR stack</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6 max-w-3xl">
            Three always-on systems replace your PR retainer.
          </h2>
          <p className="text-white/75 leading-relaxed text-pretty max-w-3xl mb-12">
            Data extraction, syndication, and authority lift — running continuously in the
            background while you focus on running the business.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => (
              <div
                key={p.label}
                className="glass-card p-6 hover-lift hover:-translate-y-1 relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <GlassIcon icon={p.icon} />
                  <span className="text-xs uppercase tracking-wider text-white/40 tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{p.label}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORBIT LOOP */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <SectionEyebrow className="justify-center">
            How programmatic syndication works
          </SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance max-w-3xl mx-auto mb-4">
            From raw market signals to permanent backlinks — in 5 phases.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            A continuous loop that captures intelligence, drafts authority assets, and syndicates
            them across premium endpoints without a single human bottleneck.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <OrbitLoop
            steps={[
              {
                label: "Aggregate Intelligence",
                description:
                  "Axy.digital continuously captures raw conversational query trends and positioning shifts across your industry vertical.",
                icon: Radar,
              },
              {
                label: "Synthesize Market Reports",
                description:
                  "The engine converts those raw signals into a publication-ready, data-rich Thought Leadership Market Report tailored to your brand's unique point of view.",
                icon: FileText,
              },
              {
                label: "Structure for SEO Authority",
                description:
                  "The system automatically injects advanced schema structures, optimized keyword targets, and backlink anchors into the report architecture to maximize crawl efficiency.",
                icon: Code2,
              },
              {
                label: "Review and Verify",
                description:
                  "Axy.digital delivers the finalized data report directly to your dashboard. Review the insights, make any brand adjustments, and hit Approve in one click.",
                icon: CheckCircle2,
              },
              {
                label: "Programmatic Syndication",
                description:
                  "The engine pushes your approved report out to premium syndication endpoints, immediately generating high-equity backlinks and scaling your digital authority footprint.",
                icon: Send,
              },
            ]}
          />
          <div className="mt-8 flex justify-center">
            <span className="glass-pill px-4 py-2 text-xs text-white/85 inline-flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />0 manual pitches
              · Syndication running on autopilot
            </span>
          </div>
        </div>
      </section>

      {/* REPORT PREVIEW — SECONDARY CTA */}
      <section id="report-preview-zone" className="px-6 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 md:p-12">
            <SectionEyebrow>Free market report preview</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-4">
              🕵️‍♂️ Want to see the exact press release Axy.digital can programmatically syndicate for
              your brand?
            </h2>
            <p className="text-white/70 max-w-2xl mb-8 text-pretty">
              Stop letting your competitors claim the top citation slots on newswires and search
              models. Enter your domain below and our engine will run a live industry analysis to
              generate a free, data-backed Market Report preview optimized for backlink generation.
            </p>
            <ReportPreviewForm />
            <p className="mt-4 text-xs text-white/50">
              We'll email your tailored report preview within minutes.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsMasonry
        eyebrow="Founders and marketers programmatically growing authority with Axy.digital"
        heading="Compounding citations, not vanity coverage."
        items={[
          {
            quote:
              "Axy.digital helped us 5x our content output without sounding generic. The content is detailed, sharp, and actually reflects how we want to show up as an early-stage team.",
            name: "Aurelien Vasinis",
            role: "Founder & CEO",
            company: "Kuration AI",
            initials: "AV",
            hue: 295,
            metric: { value: "60+ hrs", label: "saved monthly" },
            featured: true,
          },
          {
            quote:
              "In 90 days, Axy.digital helped us go from invisible in AI answers to ranking #3 against incumbents with a decade of SEO dominance.",
            name: "Savannah Thatcher",
            role: "CMO",
            company: "Wingbits",
            initials: "ST",
            hue: 320,
            metric: { value: "11×", label: "AI visibility growth" },
          },
          {
            quote:
              "The depth of the content Axy.digital generated from very little input was genuinely impressive. Not having to prompt or configure everything manually was a game changer.",
            name: "Florian Rennberger",
            role: "Head of Sales",
            company: "Conducttr Germany",
            initials: "FR",
            hue: 270,
          },
        ]}
      />

      {/* FINAL CTA */}
      <section className="relative px-6 py-24 border-t border-neutral-800 bg-neutral-950 overflow-hidden">
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-mono">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
              </span>
              Syndication Engine: Online
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance mb-5">
            High-quality backlinks and domain authority on autopilot.
          </h2>
          <p className="text-lg text-white/70 max-w-xl mx-auto mb-12 text-pretty">
            Axy.digital packages in-depth market insights on your industry into ready-to-publish
            market reports and automatically distributes them to premium newswire sites to build
            your domain authority and ranking. No PR agencies, no pitching, no manual writing
            required.
          </p>

          {/* Performance Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 rounded-2xl overflow-hidden mb-12 bg-white/[0.02]">
            {PERFORMANCE_MATRIX.map((m, i) => (
              <div
                key={m.label}
                className={[
                  "px-5 py-6 text-left",
                  i < PERFORMANCE_MATRIX.length - 1 ? "md:border-r border-white/10" : "",
                  i < PERFORMANCE_MATRIX.length - 1 ? "border-b md:border-b-0" : "",
                ].join(" ")}
              >
                <div className="text-xs uppercase tracking-wider text-white/45 mb-2">{m.label}</div>
                <div className="text-2xl md:text-3xl font-semibold text-white mb-1">{m.value}</div>
                <div className="text-xs text-neutral-400">{m.proof}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            <Link
              to="/lp/intelligent-pr/pricing"
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Deploy Intelligent PR
              <ArrowRight className="size-4" />
            </Link>
            <p className="text-xs text-white/55">
              We open onboarding slots weekly to protect domain health.
            </p>
          </div>

          <div className="mt-10 text-sm text-white/55">
            Want to see what a sample report for your business looks like first?{" "}
            <button
              type="button"
              onClick={() => scrollToSection("report-preview-zone")}
              className="text-white/85 underline underline-offset-4 hover:text-white transition-colors"
            >
              Preview My Free Market Report
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
